import React from "react";

import { useMe } from "@hooks/queries/use-me";

import { cn } from "../../utils/ui";
import { LogoHeader } from "./components/LogoHeader";
import { NavigationLinks } from "./components/NavigationLinks";
import { UserProfileSection } from "./components/UserProfileSection";
import { useSidebar } from "./store";

interface SideNavigationProps {
  className?: string;
}

export const SideNavigation: React.FC<SideNavigationProps> = ({ className }) => {
  const { isExpanded } = useSidebar();
  const { data: me } = useMe();
  // Every user type shares the dark navy sidebar chrome (branding update —
  // "Half-Dark" shell: permanent dark nav, light content). `dark` on the
  // subtree lets the components' `dark:` variants supply light text. The navy
  // background uses `bg-primary` (fixed brand navy, not theme-dependent —
  // see index.css) rather than a `dark:` bg util, since that needs an
  // ancestor with the class, not self — and it keeps the sidebar navy the
  // same token as the tooltip/popover chrome instead of a one-off hex.
  const userType = me?.user?.user_type;
  const isProvider = userType === "provider";
  const isPortal = isProvider || userType === "referrer";

  return (
    <nav
      className={cn(
        "dark relative m-2 grid grid-rows-[auto_1fr_auto] overflow-hidden rounded-xl border border-transparent bg-primary shadow-md",
        "duration-[220ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] transition-[width]",
        // External portals get the design's wider 250px sidebar; everyone else
        // keeps the 200px app sidebar (68px collapsed for all).
        isExpanded ? (isPortal ? "w-[250px]" : "w-[200px]") : "w-[68px]",
        className
      )}
    >
      <LogoHeader />

      <NavigationLinks />

      {/* Live-sync status note — provider portal only (SureMed middleware). */}
      {isProvider && isExpanded && (
        <div className="mx-3 mb-1 rounded-[11px] border border-[rgba(0,255,255,0.14)] bg-[rgba(0,255,255,0.05)] p-3">
          <div className="flex items-center gap-[7px]">
            <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-[#33e0c0] shadow-[0_0_0_3px_rgba(51,224,192,0.22)]" />
            <span className="text-[11px] font-bold tracking-[0.02em] text-[#8ff0e6]">
              MEDirect API · live
            </span>
          </div>
          <div className="mt-[7px] text-[10.5px] leading-[1.5] text-[#9aa6c7]">
            Requests sync in real time from referrers through the MEDirect
            middleware.
          </div>
        </div>
      )}

      <UserProfileSection />
    </nav>
  );
};
